import { useState, useEffect } from 'react';
import { ShoppingCart, Search, Loader2, X, Filter, BookOpen, Tag, SlidersHorizontal } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { fetchProducts } from '../services/api';

const SYLLABUS_STYLE = {
  Local:     'bg-green-500/10 text-green-400 border-green-500/20',
  Cambridge: 'bg-blue-500/10 text-blue-400 border-blue-500/20',
  Edexcel:   'bg-orange-500/10 text-orange-400 border-orange-500/20',
};

const optStyle = { backgroundColor: '#0d1117', color: '#e5e7eb' };

// ── Product Card ──────────────────────────────────────────────────────────────
const ProductCard = ({ product, onAdd }) => {
  const [added, setAdded] = useState(false);
  const navigate = useNavigate();
  const isOut = product.stock === 0;

  const handleAdd = () => {
    if (isOut) return;
    onAdd(product);
    setAdded(true);
    setTimeout(() => setAdded(false), 1800);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      className="group bg-darkBg border border-white/10 rounded-2xl overflow-hidden flex flex-col hover:border-accentBlue/30 hover:shadow-lg hover:shadow-accentBlue/5 transition-all duration-300"
    >
      {/* Image */}
      <div className="relative h-48 bg-white/5 overflow-hidden shrink-0">
        {product.imageUrl ? (
          <img
            src={product.imageUrl}
            alt={product.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          />
        ) : (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-700">
            <BookOpen size={32} className="mb-1.5" />
            <span className="text-xs">No Preview</span>
          </div>
        )}

        {/* Syllabus badge */}
        <span className={`absolute top-3 left-3 text-[10px] font-bold px-2.5 py-1 rounded-full border ${SYLLABUS_STYLE[product.syllabus] || 'bg-white/10 text-gray-300 border-white/20'}`}>
          {product.syllabus}
        </span>

        {/* Out of stock overlay */}
        {isOut && (
          <div className="absolute inset-0 bg-black/50 flex items-center justify-center">
            <span className="px-3 py-1.5 bg-red-500/90 text-white text-xs font-bold rounded-full">Out of Stock</span>
          </div>
        )}
      </div>

      {/* Info */}
      <div className="p-4 flex flex-col flex-1">
        {/* Subject + Level */}
        <div className="flex items-center gap-2 mb-2 flex-wrap">
          <span className="text-[10px] text-accentBlue font-semibold flex items-center gap-1">
            <Tag size={9} />{product.subject}
          </span>
          <span className="text-[10px] text-gray-500">·</span>
          <span className="text-[10px] text-gray-500">{product.level}</span>
        </div>

        {/* Title */}
        <h3 className="text-sm font-bold text-white leading-snug mb-2 line-clamp-2 flex-1">{product.title}</h3>

        {/* Description */}
        <p className="text-xs text-gray-500 line-clamp-2 mb-4 leading-relaxed">{product.description}</p>

        {/* Price + Actions */}
        <div className="flex items-center justify-between gap-2 pt-3 border-t border-white/8 mt-auto">
          <p className="text-base font-extrabold text-accentBlue">Rs. {Number(product.price).toLocaleString()}</p>
          <div className="flex items-center gap-2">
            <button
              onClick={() => navigate(`/shop/${product._id}`)}
              className="text-xs font-semibold text-gray-400 hover:text-white px-3 py-1.5 rounded-lg border border-white/10 hover:border-white/25 transition-all"
            >
              Details
            </button>
            <button
              onClick={handleAdd}
              disabled={isOut}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                added
                  ? 'bg-emerald-500/20 border border-emerald-500/30 text-emerald-400'
                  : isOut
                  ? 'bg-white/5 border border-white/10 text-gray-600 cursor-not-allowed'
                  : 'bg-accentBlue text-black hover:opacity-90'
              }`}
            >
              <ShoppingCart size={13} />
              {added ? 'Added!' : 'Add'}
            </button>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

// ── Main ──────────────────────────────────────────────────────────────────────
const ShopPage = () => {
  const { addToCart } = useCart();

  const [products,    setProducts]    = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [filter, setFilter] = useState({
    search: '', syllabus: '', subject: '', level: '', maxPrice: '',
  });

  useEffect(() => {
    fetchProducts()
      .then(res => setProducts(res.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const subjects = [...new Set(products.map(p => p.subject))].sort();
  const levels   = [...new Set(products.map(p => p.level))].sort();
  const maxPrice = Math.max(...products.map(p => Number(p.price)), 10000);

  const filtered = products.filter(p => {
    if (!p.isAvailable) return false;
    if (filter.syllabus && p.syllabus !== filter.syllabus) return false;
    if (filter.subject  && p.subject  !== filter.subject)  return false;
    if (filter.level    && p.level    !== filter.level)    return false;
    if (filter.maxPrice && Number(p.price) > Number(filter.maxPrice)) return false;
    const q = filter.search.toLowerCase();
    if (q && !p.title.toLowerCase().includes(q) && !p.subject.toLowerCase().includes(q)) return false;
    return true;
  });

  const activeCount = [filter.syllabus, filter.subject, filter.level, filter.maxPrice].filter(Boolean).length;
  const clearFilters = () => setFilter(f => ({ ...f, syllabus: '', subject: '', level: '', maxPrice: '' }));

  // ── Filter Panel (shared between sidebar + mobile drawer) ──────────────────
  const FilterPanel = () => (
    <div className="space-y-6">
      {/* Syllabus */}
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Syllabus</p>
        <div className="space-y-2">
          {['', 'Local', 'Cambridge', 'Edexcel'].map(s => (
            <button key={s} onClick={() => setFilter(f => ({ ...f, syllabus: s }))}
              className={`w-full text-left px-3 py-2 rounded-xl text-sm font-medium transition-all ${
                filter.syllabus === s
                  ? 'bg-accentBlue/15 text-accentBlue border border-accentBlue/30'
                  : 'text-gray-400 hover:bg-white/5 hover:text-white border border-transparent'
              }`}>
              {s || 'All Syllabuses'}
            </button>
          ))}
        </div>
      </div>

      {/* Subject */}
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Subject</p>
        <select
          value={filter.subject}
          onChange={e => setFilter(f => ({ ...f, subject: e.target.value }))}
          className="w-full bg-white/5 border border-white/10 p-2.5 rounded-xl text-sm outline-none focus:border-accentBlue transition-all"
          style={optStyle}
        >
          <option value="" style={optStyle}>All Subjects</option>
          {subjects.map(s => <option key={s} value={s} style={optStyle}>{s}</option>)}
        </select>
      </div>

      {/* Level */}
      <div>
        <p className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Level</p>
        <div className="flex flex-wrap gap-2">
          {['', ...levels].map(l => (
            <button key={l} onClick={() => setFilter(f => ({ ...f, level: l }))}
              className={`px-3 py-1.5 rounded-lg text-xs font-bold border transition-all ${
                filter.level === l
                  ? 'bg-accentBlue/15 text-accentBlue border-accentBlue/30'
                  : 'bg-white/5 text-gray-400 border-white/10 hover:border-white/20 hover:text-white'
              }`}>
              {l || 'All'}
            </button>
          ))}
        </div>
      </div>

      {/* Price Range */}
      <div>
        <div className="flex justify-between items-center mb-3">
          <p className="text-xs font-bold text-gray-400 uppercase tracking-wider">Max Price</p>
          <span className="text-xs font-bold text-accentBlue">
            {filter.maxPrice ? `Rs. ${Number(filter.maxPrice).toLocaleString()}` : 'Any'}
          </span>
        </div>
        <input
          type="range" min="0" max={maxPrice} step="100"
          value={filter.maxPrice || maxPrice}
          className="w-full accent-accentBlue"
          onChange={e => setFilter(f => ({ ...f, maxPrice: Number(e.target.value) >= maxPrice ? '' : e.target.value }))}
        />
        <div className="flex justify-between text-[10px] text-gray-600 mt-1">
          <span>Rs. 0</span><span>Rs. {maxPrice.toLocaleString()}</span>
        </div>
      </div>

      {/* Clear */}
      {activeCount > 0 && (
        <button onClick={clearFilters}
          className="w-full py-2.5 text-xs font-bold text-red-400 border border-red-400/20 rounded-xl hover:bg-red-400/8 transition-colors flex items-center justify-center gap-1.5">
          <X size={12} /> Clear Filters ({activeCount})
        </button>
      )}
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-5 py-10">

      {/* ── Page Header ── */}
      <div className="mb-8">
        <h1 className="text-3xl font-extrabold text-white mb-1">Books & Materials</h1>
        <p className="text-gray-400 text-sm">Past paper books, revision notes and study materials.</p>
      </div>

      {/* ── Search + Filter Toggle ── */}
      <div className="flex gap-3 mb-8">
        <div className="relative flex-1">
          <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" size={17} />
          <input
            type="text"
            placeholder="Search by title or subject…"
            value={filter.search}
            onChange={e => setFilter(f => ({ ...f, search: e.target.value }))}
            className="w-full bg-white/5 border border-white/10 py-3 pl-11 pr-10 rounded-xl text-sm text-white placeholder:text-gray-500 outline-none focus:border-accentBlue focus:ring-2 focus:ring-accentBlue/10 transition-all"
          />
          {filter.search && (
            <button onClick={() => setFilter(f => ({ ...f, search: '' }))}
              className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 hover:text-white transition-colors">
              <X size={15} />
            </button>
          )}
        </div>
        {/* Filter toggle (mobile) */}
        <button
          onClick={() => setSidebarOpen(v => !v)}
          className={`lg:hidden flex items-center gap-2 px-4 py-3 rounded-xl text-sm font-bold border transition-all ${
            activeCount > 0 || sidebarOpen
              ? 'bg-accentBlue/10 border-accentBlue/30 text-accentBlue'
              : 'bg-white/5 border-white/10 text-gray-400'
          }`}
        >
          <SlidersHorizontal size={16} />
          Filters {activeCount > 0 && <span className="w-5 h-5 rounded-full bg-accentBlue text-black text-[10px] font-bold flex items-center justify-center">{activeCount}</span>}
        </button>
      </div>

      {/* ── Mobile filter drawer ── */}
      <AnimatePresence>
        {sidebarOpen && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }} exit={{ opacity: 0, height: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden mb-6 bg-white/4 border border-white/10 rounded-2xl p-5">
            <FilterPanel />
          </motion.div>
        )}
      </AnimatePresence>

      <div className="flex gap-8">

        {/* ── Desktop sidebar ── */}
        <aside className="hidden lg:block w-56 shrink-0">
          <div className="sticky top-24 bg-white/4 border border-white/10 rounded-2xl p-5">
            <div className="flex items-center justify-between mb-5">
              <h3 className="font-bold text-white text-sm flex items-center gap-2"><Filter size={14} className="text-accentBlue" /> Filters</h3>
              {activeCount > 0 && (
                <span className="text-[10px] font-bold text-accentBlue bg-accentBlue/10 border border-accentBlue/20 px-2 py-0.5 rounded-full">
                  {activeCount} active
                </span>
              )}
            </div>
            <FilterPanel />
          </div>
        </aside>

        {/* ── Product Grid ── */}
        <main className="flex-1 min-w-0">
          {/* Result count + active filters */}
          <div className="flex items-center justify-between mb-5 flex-wrap gap-2">
            <p className="text-sm text-gray-400">
              {loading ? 'Loading…' : <><span className="text-white font-bold">{filtered.length}</span> {filtered.length === 1 ? 'product' : 'products'} found</>}
            </p>
            {/* Active filter chips */}
            <div className="flex flex-wrap gap-2">
              {filter.syllabus && (
                <span className="flex items-center gap-1.5 text-xs px-3 py-1 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue rounded-full font-semibold">
                  {filter.syllabus}
                  <button onClick={() => setFilter(f => ({ ...f, syllabus: '' }))}><X size={10} /></button>
                </span>
              )}
              {filter.subject && (
                <span className="flex items-center gap-1.5 text-xs px-3 py-1 bg-white/10 border border-white/15 text-gray-300 rounded-full font-semibold">
                  {filter.subject}
                  <button onClick={() => setFilter(f => ({ ...f, subject: '' }))}><X size={10} /></button>
                </span>
              )}
              {filter.level && (
                <span className="flex items-center gap-1.5 text-xs px-3 py-1 bg-white/10 border border-white/15 text-gray-300 rounded-full font-semibold">
                  {filter.level}
                  <button onClick={() => setFilter(f => ({ ...f, level: '' }))}><X size={10} /></button>
                </span>
              )}
              {filter.maxPrice && (
                <span className="flex items-center gap-1.5 text-xs px-3 py-1 bg-white/10 border border-white/15 text-gray-300 rounded-full font-semibold">
                  Max Rs. {Number(filter.maxPrice).toLocaleString()}
                  <button onClick={() => setFilter(f => ({ ...f, maxPrice: '' }))}><X size={10} /></button>
                </span>
              )}
            </div>
          </div>

          {loading ? (
            <div className="flex flex-col items-center justify-center py-32">
              <Loader2 size={32} className="animate-spin text-accentBlue mb-4" />
              <p className="text-gray-500 text-sm">Loading products…</p>
            </div>
          ) : filtered.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-32 border border-dashed border-white/10 rounded-2xl text-center px-6">
              <BookOpen size={40} className="text-gray-700 mb-4" />
              <p className="text-white font-bold mb-1">No products found</p>
              <p className="text-gray-500 text-sm mb-6">Try changing your search or filters.</p>
              <button onClick={() => setFilter({ search: '', syllabus: '', subject: '', level: '', maxPrice: '' })}
                className="px-5 py-2.5 bg-accentBlue/10 border border-accentBlue/20 text-accentBlue text-sm font-bold rounded-xl hover:bg-accentBlue/15 transition-colors">
                Clear Everything
              </button>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-5">
              {filtered.map(product => (
                <ProductCard
                  key={product._id}
                  product={product}
                  onAdd={(p) => addToCart({ ...p, id: p._id, price: String(p.price) })}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
};

export default ShopPage;
